import React, { useState, useEffect } from 'react'
import { Clock, X, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import type { Queue } from '@/features/social/types'

interface PostSchedulerProps {
  scheduledTime: string | null
  onScheduledTimeChange: (time: string | null) => void
  queueId: string | null
  onQueueIdChange: (id: string | null) => void
  queues: Queue[]
  error?: string | null
  isSubmitting: boolean
}

export function PostScheduler({
  scheduledTime,
  onScheduledTimeChange,
  queueId,
  onQueueIdChange,
  queues,
  error,
  isSubmitting
}: PostSchedulerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    scheduledTime ? new Date(scheduledTime) : undefined
  )
  const [selectedTime, setSelectedTime] = useState(
    scheduledTime ? format(new Date(scheduledTime), 'HH:mm') : '09:00'
  )

  useEffect(() => {
    if (scheduledTime) {
      const date = new Date(scheduledTime)
      setSelectedDate(date)
      setSelectedTime(format(date, 'HH:mm'))
    } else {
      setSelectedDate(undefined)
      setSelectedTime('09:00')
    }
  }, [scheduledTime])

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    updateScheduledTime(date, selectedTime)
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
    updateScheduledTime(selectedDate, time)
  }

  const updateScheduledTime = (date: Date | undefined, time: string) => {
    if (date && time) {
      const [hours, minutes] = time.split(':').map(Number)
      const scheduledDateTime = new Date(date)
      scheduledDateTime.setHours(hours, minutes, 0, 0)
      onScheduledTimeChange(scheduledDateTime.toISOString())
    } else {
      onScheduledTimeChange(null)
    }
  }

  const clearSchedule = () => {
    setSelectedDate(undefined)
    setSelectedTime('09:00')
    onScheduledTimeChange(null)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-2">
        <Label htmlFor="queue" className="text-sm font-medium text-muted-foreground">Queue (Optional)</Label>
        <Select
          value={queueId || 'none'}
          onValueChange={(value) => onQueueIdChange(value === 'none' ? null : value)}
          disabled={isSubmitting}
        >
          <SelectTrigger className="h-10">
            <SelectValue placeholder="Select a queue" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No queue</SelectItem>
            {queues.map((queue) => (
              <SelectItem key={queue.id} value={queue.id}>
                {queue.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-muted-foreground">Schedule (Optional)</Label>
        <div className="flex gap-2">
          <div className={cn(
            "flex-1",
            error && "ring-1 ring-red-500 rounded-md"
          )}>
            <DatePicker
              date={selectedDate}
              onDateChange={handleDateSelect}
              placeholder="Select date"
              disabled={(date) => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                return date < today || isSubmitting
              }}
              className="w-full"
            />
          </div>

          <Select value={selectedTime} onValueChange={handleTimeChange} disabled={isSubmitting}>
            <SelectTrigger
              className={cn(
                "w-32",
                error && "border-red-500"
              )}
            >
              <SelectValue placeholder="Time" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 24 }, (_, hour) => {
                const timeOptions = []
                for (let minute = 0; minute < 60; minute += 15) {
                  const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
                  timeOptions.push(
                    <SelectItem key={timeString} value={timeString}>
                      {timeString}
                    </SelectItem>
                  )
                }
                return timeOptions
              }).flat()}
            </SelectContent>
          </Select>

          {(selectedDate || scheduledTime) && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive"
              onClick={clearSchedule}
              disabled={isSubmitting}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-1 text-sm text-red-500">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}
        {scheduledTime && !error && (
          <div className="flex items-center gap-2 text-sm text-primary bg-primary/5 p-2 rounded-md border border-primary/10">
            <Clock className="h-4 w-4" />
            <span className="font-medium">Scheduled for {format(new Date(scheduledTime), 'PPp')}</span>
          </div>
        )}
      </div>
    </div>
  )
}

